import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import axios from 'axios';
import { Zap, CheckCircle, ArrowLeft, Loader2, ArrowRight, Shield, Activity, Layers } from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Pricing = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(null);
  const [checkingPayment, setCheckingPayment] = useState(false);

  const packages = [
    {
      id: 'starter',
      name: 'Starter',
      price: 297,
      description: 'Deploy your first agent',
      features: [
        '1 live agent',
        '500 AI tokens/day included',
        'Basic monitoring dashboard',
        'Email integration',
        'Blueprint versioning',
        'Community support',
      ],
      breakeven: '1 client at $300/mo covers the platform',
      margin_example: { clients: 2, rate: 500, cost: 297 },
    },
    {
      id: 'pro',
      name: 'Pro',
      price: 497,
      description: 'Scale to multiple clients',
      features: [
        '5 live agents',
        '2,000 AI tokens/day included',
        'Full monitoring + execution traces',
        'All integrations (AI, Voice, Email, CRM)',
        'Voice AI access',
        'Production guardrails',
        'Priority support',
      ],
      highlighted: true,
      breakeven: '1 client at $500/mo covers the platform',
      margin_example: { clients: 5, rate: 900, cost: 497 },
    },
    {
      id: 'agency',
      name: 'Agency',
      price: 997,
      description: 'Full infrastructure access',
      features: [
        'Unlimited live agents',
        '10,000 AI tokens/day included',
        'Full observability suite',
        'Custom guardrail configuration',
        'White-label client portal',
        'Dedicated support',
        'Custom integrations',
      ],
      breakeven: '2 clients at $500/mo covers the platform',
      margin_example: { clients: 10, rate: 900, cost: 997 },
    }
  ];

  useEffect(() => {
    const sessionId = searchParams.get('session_id');
    if (sessionId) {
      pollPaymentStatus(sessionId);
    }
  }, [searchParams]);

  const pollPaymentStatus = async (sessionId, attempts = 0) => {
    if (attempts >= 5) {
      toast.error('Payment status check timed out.');
      return;
    }
    setCheckingPayment(true);
    try {
      const res = await axios.get(`${API}/checkout/status/${sessionId}`);
      if (res.data.payment_status === 'paid') {
        toast.success('Payment successful. Infrastructure provisioned.');
        navigate('/dashboard');
        return;
      }
      if (res.data.status === 'expired') {
        toast.error('Session expired.');
        setCheckingPayment(false);
        return;
      }
      setTimeout(() => pollPaymentStatus(sessionId, attempts + 1), 2000);
    } catch (err) {
      setCheckingPayment(false);
    }
  };

  const handleSubscribe = async (packageId) => {
    if (!user) { navigate('/signup'); return; }
    setLoading(packageId);
    try {
      const res = await axios.post(`${API}/checkout`, { package_id: packageId, origin_url: window.location.origin });
      window.location.href = res.data.url;
    } catch (err) {
      toast.error('Checkout failed.');
      setLoading(null);
    }
  };

  if (checkingPayment) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-emerald-500 animate-spin mx-auto mb-4" />
          <h2 className="text-xl font-bold mb-2">Provisioning infrastructure...</h2>
          <p className="text-muted-foreground">Confirming payment and activating your environment.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505] relative">
      <div className="noise-overlay" />

      <nav className="border-b border-white/5">
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-2">
            <HSLogo size={32} />
            <span className="font-black text-xl tracking-tight">HumanScaled</span>
          </button>
          <Button onClick={() => navigate(user ? '/dashboard' : '/login')} variant="ghost" className="text-muted-foreground hover:text-white">
            {user ? 'Dashboard' : 'Login'}
          </Button>
        </div>
      </nav>

      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-16">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-muted-foreground hover:text-white mb-8">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <div className="text-center mb-6">
          <h1 className="text-4xl md:text-5xl font-bold tracking-tight mb-3">Controlled AI Infrastructure</h1>
          <p className="text-muted-foreground text-lg max-w-xl mx-auto">
            Live agents. Monitored runtime. Human oversight. Pick the capacity you need.
          </p>
          <p className="text-sm text-white/40 mt-2">Human control. Machine execution. Measured results.</p>
        </div>

        {/* What's included */}
        <div className="flex justify-center gap-6 mb-12 flex-wrap">
          {[
            { icon: Layers, label: 'Integration layer' },
            { icon: Activity, label: 'Monitoring + traces' },
            { icon: Shield, label: 'Production guardrails' },
          ].map((item, i) => {
            const Icon = item.icon;
            return (
              <div key={i} className="flex items-center gap-2 text-sm text-muted-foreground">
                <Icon className="w-4 h-4 text-emerald-400" />
                <span>{item.label}</span>
              </div>
            );
          })}
        </div>

        {/* Packages */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mb-16">
          {packages.map((pkg) => {
            const margin = (pkg.margin_example.clients * pkg.margin_example.rate) - pkg.margin_example.cost;
            return (
              <div
                key={pkg.id}
                className={`relative border transition-all hover:-translate-y-1 ${
                  pkg.highlighted
                    ? 'bg-emerald-500/5 border-emerald-500/30'
                    : 'bg-[#0a0a0a] border-white/5 hover:border-white/10'
                }`}
              >
                {pkg.highlighted && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-0.5 bg-emerald-500 text-black text-[10px] font-bold uppercase tracking-wider">
                    Recommended
                  </div>
                )}
                <div className="p-8">
                  <h3 className="text-xl font-bold mb-1">{pkg.name}</h3>
                  <p className="text-sm text-muted-foreground mb-4">{pkg.description}</p>
                  <div className="mb-6">
                    <span className="font-mono text-4xl font-bold text-emerald-400">${pkg.price}</span>
                    <span className="text-muted-foreground">/mo</span>
                  </div>
                  <ul className="space-y-2.5 mb-6">
                    {pkg.features.map((f, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm">
                        <CheckCircle className="w-3.5 h-3.5 text-emerald-400 flex-shrink-0 mt-0.5" />
                        <span className="text-muted-foreground">{f}</span>
                      </li>
                    ))}
                  </ul>

                  {/* Break-even */}
                  <div className="border-t border-white/5 pt-4 mb-4">
                    <p className="text-[10px] text-muted-foreground uppercase tracking-wider mb-2">Leverage Example</p>
                    <div className="font-mono text-xs space-y-1">
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">{pkg.margin_example.clients} clients x ${pkg.margin_example.rate}/mo</span>
                        <span>${(pkg.margin_example.clients * pkg.margin_example.rate).toLocaleString()}</span>
                      </div>
                      <div className="flex justify-between">
                        <span className="text-muted-foreground">Platform</span>
                        <span className="text-red-400">-${pkg.margin_example.cost}</span>
                      </div>
                      <div className="flex justify-between border-t border-white/10 pt-1">
                        <span className="font-bold">Margin</span>
                        <span className="text-emerald-400 font-bold">${margin.toLocaleString()}/mo</span>
                      </div>
                    </div>
                    <p className="text-[10px] text-muted-foreground mt-2">{pkg.breakeven}</p>
                  </div>

                  <Button
                    onClick={() => handleSubscribe(pkg.id)}
                    disabled={loading === pkg.id}
                    className={`w-full h-11 font-bold rounded-sm ${
                      pkg.highlighted
                        ? 'bg-emerald-500 text-black hover:bg-emerald-400'
                        : 'bg-white/5 hover:bg-white/10 border border-white/10'
                    }`}
                    data-testid={`subscribe-${pkg.id}-btn`}
                  >
                    {loading === pkg.id ? <Loader2 className="w-5 h-5 animate-spin" /> : user ? <>Get Started <ArrowRight className="ml-2 w-4 h-4" /></> : 'Sign Up'}
                  </Button>
                </div>
              </div>
            );
          })}
        </div>

        <div className="text-center space-y-2">
          <p className="text-sm text-muted-foreground">Secure payment via Stripe. Cancel anytime. No long-term contracts.</p>
          <p className="text-xs text-muted-foreground">Usage beyond included credits billed at standard rates. All plans include full guardrail suite.</p>
        </div>
      </div>
    </div>
  );
};

export default Pricing;
